// Types
import type { TBaseImportRow, TImportStats } from '~/shared/import/types';
import type { IVDropdownOption } from '~/misc/v-dropdown/types';
import type { ICsvRowGenerator } from '~/shared/import/services/csv-row-generator/types';
import { EImportParamKey, EMappingVariant, ESourceType } from '~/shared/import/types';

// Services
import { UprnCsvRowGenerator } from '~/shared/import/services/csv-row-generator/strategies/uprn';
import { SingleAddressCsvRowGenerator } from '~/shared/import/services/csv-row-generator/strategies/single-address';
import { MultipleAddressCsvRowGenerator } from '~/shared/import/services/csv-row-generator/strategies/multiple-address';

const DEFAULT_IMPORT_STATS: TImportStats = {
  added: 0,
  updated: 0,
  invalid: 0,
  failed: 0,
};

const DEFAULT_MAPPING_VARIANT: EMappingVariant = EMappingVariant.UPRN;

const DEFAULT_SOURCE_TYPE: ESourceType = ESourceType.NONE;

const DEFAULT_IGNORED_COLUMNS: EImportParamKey[] = [
  EImportParamKey.CONTACT_TYPE,
  EImportParamKey.CONTACT_REFERENCE,
  EImportParamKey.CONTACT_NAME,
  EImportParamKey.CONTACT_EMAIL,
  EImportParamKey.CONTACT_MARKETING_CONSENT,
  EImportParamKey.CONTACT_MOBILE_PHONE,
  EImportParamKey.CONTACT_WORK_PHONE,
  EImportParamKey.CONTACT_HOME_PHONE,
  EImportParamKey.CONTACT_CREATED,
];

const TABLE_HEADERS_MAP: Record<EImportParamKey, string> = {
  [EImportParamKey.UPRN]: 'UPRN',
  [EImportParamKey.ADDRESS]: 'Address',
  [EImportParamKey.ADDRESS_1]: 'Address 1',
  [EImportParamKey.ADDRESS_2]: 'Address 2',
  [EImportParamKey.ADDRESS_3]: 'Address 3',
  [EImportParamKey.ADDRESS_4]: 'Address 4',
  [EImportParamKey.ADDRESS_5]: 'Address 5',
  [EImportParamKey.ADDRESS_6]: 'Address 6',
  [EImportParamKey.ADDRESS_7]: 'Address 7',
  [EImportParamKey.POSTCODE]: 'Postcode',
  [EImportParamKey.CUSTOMER_REFERENCE]: 'Customer reference',
  [EImportParamKey.CRM_LINK]: 'CRM Link',
  [EImportParamKey.LAST_ACTIVITY]: 'Last activity',
  [EImportParamKey.LAST_VALUATION]: 'Last valuation',
  [EImportParamKey.LAST_LETTING_VALUATION]: 'Last letting valuation',
  [EImportParamKey.SOURCE]: 'Source',
  [EImportParamKey.CONTACT_TYPE]: 'Contact type',
  [EImportParamKey.CONTACT_REFERENCE]: 'Contact reference',
  [EImportParamKey.CONTACT_NAME]: 'Contact name',
  [EImportParamKey.CONTACT_EMAIL]: 'Contact email',
  [EImportParamKey.CONTACT_MARKETING_CONSENT]: 'Contact marketing consent',
  [EImportParamKey.CONTACT_MOBILE_PHONE]: 'Contact mobile phone',
  [EImportParamKey.CONTACT_WORK_PHONE]: 'Contact work phone',
  [EImportParamKey.CONTACT_HOME_PHONE]: 'Contact home phone',
  [EImportParamKey.CONTACT_CREATED]: 'Contact created',
  [EImportParamKey.RECORD_CREATED_DATE]: 'Created date',
};

const UPRN_TABLE_HEADERS_LIST: string[] = [
  EImportParamKey.UPRN,
  EImportParamKey.POSTCODE,
  EImportParamKey.CUSTOMER_REFERENCE,
  EImportParamKey.CRM_LINK,
  EImportParamKey.LAST_ACTIVITY,
  EImportParamKey.LAST_VALUATION,
  EImportParamKey.LAST_LETTING_VALUATION,
  EImportParamKey.RECORD_CREATED_DATE,
  EImportParamKey.SOURCE,
  EImportParamKey.CONTACT_TYPE,
  EImportParamKey.CONTACT_REFERENCE,
  EImportParamKey.CONTACT_NAME,
  EImportParamKey.CONTACT_EMAIL,
  EImportParamKey.CONTACT_MARKETING_CONSENT,
  EImportParamKey.CONTACT_MOBILE_PHONE,
  EImportParamKey.CONTACT_WORK_PHONE,
  EImportParamKey.CONTACT_HOME_PHONE,
  EImportParamKey.CONTACT_CREATED,
];

const SINGLE_ADDRESS_TABLE_HEADERS_LIST: string[] = [
  EImportParamKey.ADDRESS,
  EImportParamKey.POSTCODE,
  EImportParamKey.CUSTOMER_REFERENCE,
  EImportParamKey.CRM_LINK,
  EImportParamKey.LAST_ACTIVITY,
  EImportParamKey.LAST_VALUATION,
  EImportParamKey.LAST_LETTING_VALUATION,
  EImportParamKey.RECORD_CREATED_DATE,
  EImportParamKey.SOURCE,
  EImportParamKey.CONTACT_TYPE,
  EImportParamKey.CONTACT_REFERENCE,
  EImportParamKey.CONTACT_NAME,
  EImportParamKey.CONTACT_EMAIL,
  EImportParamKey.CONTACT_MARKETING_CONSENT,
  EImportParamKey.CONTACT_MOBILE_PHONE,
  EImportParamKey.CONTACT_WORK_PHONE,
  EImportParamKey.CONTACT_HOME_PHONE,
  EImportParamKey.CONTACT_CREATED,
];

const MULTI_ADDRESS_TABLE_HEADERS_LIST: string[] = [
  EImportParamKey.ADDRESS_1,
  EImportParamKey.ADDRESS_2,
  EImportParamKey.ADDRESS_3,
  EImportParamKey.ADDRESS_4,
  EImportParamKey.ADDRESS_5,
  EImportParamKey.ADDRESS_6,
  EImportParamKey.ADDRESS_7,
  EImportParamKey.POSTCODE,
  EImportParamKey.CUSTOMER_REFERENCE,
  EImportParamKey.CRM_LINK,
  EImportParamKey.LAST_ACTIVITY,
  EImportParamKey.LAST_VALUATION,
  EImportParamKey.LAST_LETTING_VALUATION,
  EImportParamKey.RECORD_CREATED_DATE,
  EImportParamKey.SOURCE,
  EImportParamKey.CONTACT_TYPE,
  EImportParamKey.CONTACT_REFERENCE,
  EImportParamKey.CONTACT_NAME,
  EImportParamKey.CONTACT_EMAIL,
  EImportParamKey.CONTACT_MARKETING_CONSENT,
  EImportParamKey.CONTACT_MOBILE_PHONE,
  EImportParamKey.CONTACT_WORK_PHONE,
  EImportParamKey.CONTACT_HOME_PHONE,
  EImportParamKey.CONTACT_CREATED,
];

const TABLE_HEADER_LISTS: Record<EMappingVariant, string[]> = {
  [EMappingVariant.UPRN]: UPRN_TABLE_HEADERS_LIST,
  [EMappingVariant.SINGLE_ADDRESS]: SINGLE_ADDRESS_TABLE_HEADERS_LIST,
  [EMappingVariant.MULTI_ADDRESS]: MULTI_ADDRESS_TABLE_HEADERS_LIST,
};

const generateHeaderOptions = (list: string[]) =>
  list.map((header: string) => ({
    label: TABLE_HEADERS_MAP[header as EImportParamKey],
    value: header,
  }));

const UPRN_HEADER_OPTIONS: IVDropdownOption<string, string>[] =
  generateHeaderOptions(UPRN_TABLE_HEADERS_LIST);

const SINGLE_ADDRESS_HEADER_OPTIONS: IVDropdownOption<string, string>[] = generateHeaderOptions(
  SINGLE_ADDRESS_TABLE_HEADERS_LIST
);

const MULTI_ADDRESS_HEADER_OPTIONS: IVDropdownOption<string, string>[] = generateHeaderOptions(
  MULTI_ADDRESS_TABLE_HEADERS_LIST
);

const HEADER_OPTIONS: Record<EMappingVariant, IVDropdownOption<string, string>[]> = {
  [EMappingVariant.UPRN]: UPRN_HEADER_OPTIONS,
  [EMappingVariant.SINGLE_ADDRESS]: SINGLE_ADDRESS_HEADER_OPTIONS,
  [EMappingVariant.MULTI_ADDRESS]: MULTI_ADDRESS_HEADER_OPTIONS,
};

const CSV_ROW_GENERATOR: Record<EMappingVariant, ICsvRowGenerator<TBaseImportRow>> = {
  [EMappingVariant.UPRN]: new UprnCsvRowGenerator(),
  [EMappingVariant.SINGLE_ADDRESS]: new SingleAddressCsvRowGenerator(),
  [EMappingVariant.MULTI_ADDRESS]: new MultipleAddressCsvRowGenerator(),
};

export {
  DEFAULT_IMPORT_STATS,
  DEFAULT_MAPPING_VARIANT,
  DEFAULT_SOURCE_TYPE,
  DEFAULT_IGNORED_COLUMNS,
  TABLE_HEADERS_MAP,
  TABLE_HEADER_LISTS,
  HEADER_OPTIONS,
  CSV_ROW_GENERATOR,
};
